import type { Recipe } from '@/types'

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, Link2, Search } from 'lucide-react'

import {
  Button,
  Input,
  RecipeSearchCard,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { useKeydownShortcut } from '@/hooks'
import { MixLayout } from '@/components/layout'
import { useWebSocketContext } from '@/contexts'
import { recipeService, mixService } from '@/services'

export default function AddRecipesPage() {
  const { mixId = '' } = useParams()
  const navigate = useNavigate()
  const { mixes, isMix } = useWebSocketContext()

  const [activeTab, setActiveTab] = useState('url')

  const mix = useMemo(() => {
    return isMix(mixId) ? mixes.find((m) => m.id === mixId) ?? null : null
  }, [mixId, isMix, mixes])

  const mixRecipeIds = useMemo(() => {
    return new Set((mix?.recipes || []).map((r) => r.id))
  }, [mix])

  // ── URL tab state ──
  const [url, setUrl] = useState('')
  const [isExtracting, setIsExtracting] = useState(false)
  const [urlError, setUrlError] = useState<string | null>(null)
  const [urlSuccess, setUrlSuccess] = useState(false)

  // ── Search tab state ──
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Recipe[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // ── Batch add state ──
  const [isBatchAdding, setIsBatchAdding] = useState(false)
  const [batchError, setBatchError] = useState<string | null>(null)

  // ── Refs for auto-focus ──
  const urlInputRef = useRef<HTMLInputElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus input when tab changes
  useEffect(() => {
    if (activeTab === 'url') {
      urlInputRef.current?.focus()
    } else if (activeTab === 'search') {
      searchInputRef.current?.focus()
    }
  }, [activeTab])

  const performSearch = useCallback(async (searchText: string) => {
    setIsSearching(true)
    setSearchError(null)
    try {
      const results = await recipeService.search(searchText)
      // Belt-and-suspenders: ensure results are ordered by popularity then recency
      // even before the DB migration is deployed.
      const sorted = results.sort((a, b) => {
        const countA = a.favoriteCount ?? 0
        const countB = b.favoriteCount ?? 0
        if (countB !== countA) return countB - countA
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      setSearchResults(sorted)
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [])

  // Load recent recipes when switching to search tab
  useEffect(() => {
    if (activeTab === 'search' && query.trim() === '' && searchResults.length === 0) {
      performSearch('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  // Debounced keyword search
  useEffect(() => {
    if (activeTab !== 'search') return
    const timer = setTimeout(() => {
      performSearch(query.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [activeTab, query, performSearch])

  const handleToggle = (recipeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(recipeId)) {
        next.delete(recipeId)
      } else {
        next.add(recipeId)
      }
      return next
    })
  }

  const handleCancel = () => {
    navigate(`/mixes/${mixId}`)
  }

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return
    setIsBatchAdding(true)
    setBatchError(null)
    try {
      const ids = Array.from(selectedIds)
      const results = await Promise.allSettled(
        ids.map((rid) => mixService.appendRecipeToMix(mixId, rid))
      )

      const failures = results
        .map((r, i) => ({ status: r.status, id: ids[i] }))
        .filter((r) => r.status === 'rejected')

      if (failures.length > 0) {
        setBatchError(`Failed to add ${failures.length} recipe(s). Please try again.`)
        // Remove successfully added ones from selection
        const succeeded = new Set(
          results
            .map((r, i) => ({ status: r.status, id: ids[i] }))
            .filter((r) => r.status === 'fulfilled')
            .map((r) => r.id)
        )
        setSelectedIds((prev) => {
          const next = new Set(prev)
          succeeded.forEach((id) => next.delete(id))
          return next
        })
      } else {
        // All succeeded — navigate back
        navigate(`/mixes/${mixId}`)
      }
    } catch (err) {
      setBatchError(err instanceof Error ? err.message : 'Failed to add recipes')
    } finally {
      setIsBatchAdding(false)
    }
  }

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!urlValid) return
    setIsExtracting(true)
    setUrlError(null)
    setUrlSuccess(false)
    try {
      await mixService.createRecipe(mixId, url.trim())
      setUrlSuccess(true)
      setUrl('')
      // Clear success message after 3s
      setTimeout(() => setUrlSuccess(false), 3000)
    } catch (err) {
      setUrlError(err instanceof Error ? err.message : 'Failed to add recipe')
    } finally {
      setIsExtracting(false)
    }
  }

  useKeydownShortcut(
    { key: 'Escape' },
    handleCancel,
    'Cancel / Go back',
    'Return to the mix recipes page'
  )

  const urlValid = useMemo(() => {
    if (!url.trim()) return false
    try {
      const u = new URL(url.trim())
      return u.protocol === 'http:' || u.protocol === 'https:'
    } catch {
      return false
    }
  }, [url])

  if (!mix) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="size-12" />
      </div>
    )
  }

  return (
    <MixLayout>
      <div className="flex flex-col min-h-0">
        {/* Sticky header */}
        <div className="sticky top-0 bg-background z-30 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCancel}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-foreground truncate">{mix.name}</h1>
              <p className="text-xs text-muted-foreground">Add Recipes</p>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-24">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2">
              <TabsTrigger value="url">
                <Link2 className="h-4 w-4 mr-2" />
                Add from URL
              </TabsTrigger>
              <TabsTrigger value="search">
                <Search className="h-4 w-4 mr-2" />
                Search Existing
              </TabsTrigger>
            </TabsList>

            {/* ── Tab: Add from URL ── */}
            <TabsContent value="url">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Paste a recipe URL and we&apos;ll extract the ingredients</p>

                <form onSubmit={handleUrlSubmit} className="flex gap-2">
                  <Input
                    ref={urlInputRef}
                    placeholder="https://example.com/recipe"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value)
                      setUrlError(null)
                      setUrlSuccess(false)
                    }}
                    disabled={isExtracting}
                    className="flex-1"
                  />
                  <Button type="submit" size="lg" disabled={!urlValid || isExtracting}>
                    {isExtracting ? (
                      <span className="flex items-center gap-2">
                        <Spinner className="size-4" />
                        Adding...
                      </span>
                    ) : (
                      'Add'
                    )}
                  </Button>
                </form>

                {isExtracting && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner className="size-4" />
                    <span>Fetching and extracting recipe... This may take a moment.</span>
                  </div>
                )}

                {urlSuccess && !isExtracting && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Recipe added! It will appear in your mix shortly.</span>
                  </div>
                )}

                {urlError && !isExtracting && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>{urlError}</span>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ── Tab: Search Existing ── */}
            <TabsContent value="search">
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    placeholder="Search by recipe name or ingredient..."
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value)
                      setSearchError(null)
                    }}
                    className="pl-10"
                  />
                </div>

                {isSearching && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                    <Spinner className="size-4" />
                    Searching...
                  </div>
                )}

                {searchError && !isSearching && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 mb-3">
                      <AlertTriangle className="h-6 w-6 text-red-500" />
                    </div>
                    <h3 className="text-base font-medium text-foreground">Search failed</h3>
                  </div>
                )}

                {!isSearching && !searchError && searchResults.length === 0 && query && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No recipes found for &quot;{query}&quot;.</p>
                    <p className="text-sm mt-1">Try a different search or add a new URL above.</p>
                  </div>
                )}

                {!isSearching && !searchError && searchResults.length > 0 && (
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {searchResults.map((recipe) => (
                      <RecipeSearchCard
                        key={recipe.id}
                        recipe={recipe}
                        selected={selectedIds.has(recipe.id)}
                        alreadyInMix={mixRecipeIds.has(recipe.id)}
                        onToggle={() => handleToggle(recipe.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sticky action bar */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card p-4 shadow-lg text-foreground">
            <div className="w-full max-w-[768px] mx-auto flex items-center justify-between gap-4 px-4">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {selectedIds.size} selected
                </span>
                {batchError && (
                  <span className="text-xs text-red-500 mt-0.5">{batchError}</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancel} disabled={isBatchAdding}>
                  Cancel
                </Button>
                <Button onClick={handleAddSelected} disabled={isBatchAdding}>
                  {isBatchAdding ? (
                    <span className="flex items-center gap-2">
                      <Spinner className="size-4" />
                      Adding...
                    </span>
                  ) : (
                    'Add Selected →'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MixLayout>
  )
}
